import type { TargetSelection, WizardState } from '../store/wizardTypes';
import type { AudienceTarget, CreateAudienceRequest, LookalikeRatio } from '../types';
import { LOOKALIKE_RATIOS } from '../types';
import { toRequestFilters } from './filters';

export type BuildCreateRequestInput = {
  wizard: Pick<
    WizardState,
    | 'name'
    | 'description'
    | 'filters'
    | 'brandSharePercent'
    | 'acceptedPreview'
    | 'prompt'
    | 'clonedNlPrompt'
  >;
  targets: TargetSelection;
};

/**
 * Builds the `createAudience` body from the wizard: the definition and its first delivery.
 *
 * - Filters travel verbatim from the edited set, nulls included.
 * - `nlPrompt` is the accepted preview's `userRequest` echo, so it matches what produced the filters.
 * - With no accepted preview, a clone whose prompt text is unchanged keeps its source's `nlPrompt`.
 * - Otherwise `nlPrompt` is null.
 * - `description` is omitted when blank.
 * - A blank brand share sends null, which takes the service's default.
 * - `targets` comes from the Deliver step's selection through `buildTargets`.
 */
export const buildCreateRequest = ({
  wizard,
  targets
}: BuildCreateRequestInput): CreateAudienceRequest => {
  const description = wizard.description.trim();
  return {
    ...toRequestFilters(wizard.filters),
    name: wizard.name.trim(),
    ...(description ? { description } : {}),
    brandSharePercent: wizard.brandSharePercent,
    nlPrompt: nlPromptFor(wizard),
    targets: buildTargets(targets)
  };
};

/**
 * Builds the `targets` body of a create or a delivery from the Deliver-to selection.
 *
 * `lookalikeRatios` is on the target only when at least one ratio is picked.
 */
export const buildTargets = (targets: TargetSelection): AudienceTarget[] => {
  const ratios = sortRatios(targets.lookalikeRatios);
  return [
    {
      platform: targets.platform,
      adAccountIds: [...targets.adAccountIds],
      ...(ratios.length > 0 ? { lookalikeRatios: ratios } : {})
    }
  ];
};

const nlPromptFor = (wizard: BuildCreateRequestInput['wizard']): string | null => {
  if (wizard.acceptedPreview) return wizard.acceptedPreview.userRequest;
  return wizard.clonedNlPrompt != null && wizard.prompt === wizard.clonedNlPrompt
    ? wizard.clonedNlPrompt
    : null;
};

/* Ratios in enum order, so the request is stable regardless of click order */
const sortRatios = (ratios: LookalikeRatio[]) =>
  LOOKALIKE_RATIOS.filter(ratio => ratios.includes(ratio));
